// ============================================
// Player Store - Video Player State
// Single Responsibility: Video player management
// ============================================

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { MediaDetails } from '../types';

interface PlayerState {
    // Current media
    currentMedia: MediaDetails | null;
    currentMediaType: 'movie' | 'tv' | null;

    // TV Show specific
    selectedSeason: number;
    selectedEpisode: number;

    // Player controls
    isPlaying: boolean;
    isFullscreen: boolean;
    showPlayer: boolean;
    volume: number;

    // Trailer
    trailerKey: string | null;
    showTrailer: boolean;

    // Actions
    setCurrentMedia: (media: MediaDetails, type: 'movie' | 'tv') => void;
    setSelectedSeason: (season: number) => void;
    setSelectedEpisode: (episode: number) => void;
    setIsPlaying: (playing: boolean) => void;
    setIsFullscreen: (fullscreen: boolean) => void;
    setShowPlayer: (show: boolean) => void;
    setVolume: (volume: number) => void;
    setTrailerKey: (key: string | null) => void;
    setShowTrailer: (show: boolean) => void;
    reset: () => void;
}

const initialState = {
    currentMedia: null,
    currentMediaType: null,
    selectedSeason: 1,
    selectedEpisode: 1,
    isPlaying: false,
    isFullscreen: false,
    showPlayer: true,
    volume: 1,
    trailerKey: null,
    showTrailer: false,
};

export const usePlayerStore = create<PlayerState>()(
    immer((set) => ({
        ...initialState,

        setCurrentMedia: (media, type) => set((state) => {
            state.currentMedia = media;
            state.currentMediaType = type;
        }),

        setSelectedSeason: (season) => set((state) => {
            state.selectedSeason = season;
            state.selectedEpisode = 1; // Reset episode when season changes
        }),

        setSelectedEpisode: (episode) => set((state) => {
            state.selectedEpisode = episode;
        }),

        setIsPlaying: (playing) => set((state) => {
            state.isPlaying = playing;
        }),

        setIsFullscreen: (fullscreen) => set((state) => {
            state.isFullscreen = fullscreen;
        }),

        setShowPlayer: (show) => set((state) => {
            state.showPlayer = show;
        }),

        setVolume: (volume) => set((state) => {
            state.volume = Math.max(0, Math.min(1, volume)); // Clamp between 0 and 1
        }),

        setTrailerKey: (key) => set((state) => {
            state.trailerKey = key;
        }),

        setShowTrailer: (show) => set((state) => {
            state.showTrailer = show;
        }),

        reset: () => set(initialState),
    }))
);
